import { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { Link, NavLink, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import {
  LayoutDashboard, FolderKanban, FileCheck2, ScrollText, Image as ImageIcon,
  Sparkles, BookOpen, LogOut, Search, Send, Upload, ShieldCheck, AlertTriangle,
  CheckCircle2, ChevronRight, Building2, Hash, BarChart3, MessageSquareText, Loader2, X,
  Inbox, Link as LinkIcon, Menu, LogIn, FileText
} from "lucide-react";
import { API } from "../lib/api";
import { fmtAUD, fmtMoney, fmtBytes } from "../lib/format";

export function PhotoChecker() {
  const [files, setFiles] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [filter, setFilter] = useState("all");
  const [lightbox, setLightbox] = useState(null);
  const [kmlBusy, setKmlBusy] = useState(false);
  const [kmlProject, setKmlProject] = useState("RAAgent Photo Locations");

  const onUpload = async (selectedFiles) => {
    if (!selectedFiles || selectedFiles.length === 0) return;
    const arr = Array.from(selectedFiles);
    // Pre-flight size guard — the ingress caps request body at ~100MB.
    // For huge SmartyGrants packages, the right tool is the Ingest Inbox.
    const totalBytes = arr.reduce((s, f) => s + (f.size || 0), 0);
    const SIZE_CAP = 80 * 1024 * 1024; // 80 MB safety threshold (ingress is 100 MB)
    if (totalBytes > SIZE_CAP) {
      setErr(
        `Upload is ${(totalBytes/1024/1024).toFixed(1)} MB — too large for the Photo Checker (max ~80 MB). ` +
        `For full SmartyGrants packages, use the Ingest Inbox instead (it handles big ZIPs + unpacks nested archives + checks photos automatically per defect). ` +
        `Or split the photos into smaller ZIPs.`
      );
      return;
    }
    setFiles(arr); setData(null); setErr(null); setLoading(true);
    try {
      const fd = new FormData();
      arr.forEach(f => fd.append("files", f));
      const r = await axios.post(`${API}/tools/photo-checker`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
      });
      setData(r.data);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 413) {
        setErr(
          "The upload is too large for this preview gateway (HTTP 413). " +
          "Use the Ingest Inbox for full SmartyGrants packages — it handles nested ZIPs and runs the photo check inline per defect."
        );
      } else {
        setErr(e?.response?.data?.detail || e?.message || "Failed to inspect photos.");
      }
    } finally {
      setLoading(false);
    }
  };

  const photos = (data?.photos || []).filter(p => filter === "all" || p.verdict === filter);
  const verdicts = data?.verdicts || {};

  const onDownloadKml = async () => {
    if (!files || files.length === 0) {
      setErr("Upload photos first, then download the KML.");
      return;
    }
    setKmlBusy(true); setErr(null);
    try {
      const fd = new FormData();
      files.forEach(f => fd.append("files", f));
      fd.append("project_name", kmlProject || "RAAgent Photo Locations");
      const r = await axios.post(`${API}/tools/photo-checker/kml`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        responseType: "blob",
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
      });
      const blob = new Blob([r.data], { type: "application/vnd.google-earth.kml+xml" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const safe = (kmlProject || "photos").replace(/[^A-Za-z0-9._-]+/g, "_").slice(0, 60) || "photos";
      a.href = url; a.download = `${safe}.kml`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Failed to build KML.");
    } finally {
      setKmlBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="photo-checker-page">
      <div>
        <h1 className="font-heading text-3xl font-bold">Photo Checker</h1>
        <p className="text-sm text-slate-600 mt-1 max-w-3xl">
          Upload damage / completion photos (or a .zip of them) and get a defensibility check per
          image — resolution, EXIF capture date, GPS metadata, file size — plus a per-photo
          verdict of <span className="text-emerald-700 font-semibold">Pass</span>,
          <span className="text-amber-700 font-semibold"> Review</span>, or
          <span className="text-red-700 font-semibold"> Fail</span>. Useful for investigative
          assessments where photo provenance is the key evidence.
        </p>
      </div>

      <div className="card p-5">
        <label className="block">
          <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-slate-500 mb-2">Upload photos (.jpg / .png / .heic) or a .zip of photos</div>
          <input
            type="file"
            multiple
            accept="image/*,.zip,.heic,.heif"
            data-testid="photo-checker-file-input"
            onChange={(e) => onUpload(e.target.files)}
            className="block w-full text-sm text-slate-600 file:mr-4 file:px-4 file:py-2 file:rounded-md file:border-0 file:bg-slate-900 file:text-white file:cursor-pointer hover:file:bg-slate-700"
          />
          {files.length > 0 && (
            <div className="mt-2 text-[11.5px] font-mono text-slate-500">{files.length} file{files.length === 1 ? "" : "s"} selected</div>
          )}
        </label>
      </div>

      {loading && <div className="card p-5 flex items-center gap-3 text-sm text-slate-600"><Loader2 className="w-4 h-4 animate-spin"/> Inspecting photos…</div>}
      {err && <div className="card p-5 text-sm text-red-600">{err}</div>}

      {data && (
        <div className="space-y-4" data-testid="photo-checker-results">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
            <div className="bg-slate-50 border border-slate-200 rounded p-2"><div className="text-[9px] uppercase tracking-wider text-slate-500">Total</div><div className="font-mono font-semibold text-slate-800">{data.total}</div></div>
            <div className="bg-emerald-50 border border-emerald-200 rounded p-2"><div className="text-[9px] uppercase tracking-wider text-emerald-700">Pass</div><div className="font-mono font-semibold text-emerald-800">{verdicts.pass || 0}</div></div>
            <div className="bg-amber-50 border border-amber-200 rounded p-2"><div className="text-[9px] uppercase tracking-wider text-amber-700">Review</div><div className="font-mono font-semibold text-amber-800">{verdicts.review || 0}</div></div>
            <div className="bg-red-50 border border-red-200 rounded p-2"><div className="text-[9px] uppercase tracking-wider text-red-700">Fail</div><div className="font-mono font-semibold text-red-800">{verdicts.fail || 0}</div></div>
          </div>

          <div className="card p-4 bg-slate-50 border border-slate-200" data-testid="photo-checker-kml-card">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-slate-500 mb-1">KML export · Google Earth / Maps</div>
                <p className="text-[12.5px] text-slate-600 max-w-xl leading-relaxed">
                  Download a <span className="font-mono">.kml</span> grouping every photo by source folder (Damage Memo / asset),
                  with EXIF date, dimensions and a thumbnail in each placemark. Photos with no GPS metadata
                  land in an <span className="font-mono">Unverified — &lt;folder&gt;</span> sibling folder.
                </p>
              </div>
              <div className="flex items-end gap-2">
                <label className="block">
                  <div className="text-[9px] uppercase tracking-wider text-slate-500 mb-1">Project name</div>
                  <input
                    type="text"
                    value={kmlProject}
                    onChange={(e) => setKmlProject(e.target.value)}
                    className="px-2 py-1.5 border border-slate-300 rounded text-[12px] w-56"
                    data-testid="photo-checker-kml-project"
                  />
                </label>
                <button
                  onClick={onDownloadKml}
                  disabled={kmlBusy}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-slate-900 text-white text-[12px] font-semibold hover:bg-slate-700 disabled:opacity-60"
                  data-testid="photo-checker-kml-download"
                >
                  {kmlBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <FileText className="w-3.5 h-3.5"/>}
                  Download KML
                </button>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-wider text-slate-500">Show:</span>
            <select value={filter} onChange={(e) => setFilter(e.target.value)} className="px-2 py-1 border border-slate-300 rounded text-[11px]">
              <option value="all">All</option><option value="pass">Pass only</option>
              <option value="review">Review only</option><option value="fail">Fail only</option>
            </select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {photos.map((p, i) => {
              const tone = p.verdict === "pass" ? "border-emerald-200 bg-emerald-50"
                : p.verdict === "fail" ? "border-red-200 bg-red-50" : "border-amber-200 bg-amber-50";
              return (
                <div key={i} className={`border-2 rounded p-3 ${tone}`} data-testid={`photo-card-${i}`}>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0">
                      <div className="font-mono text-[11px] truncate" title={p.name}>{p.name}</div>
                      <div className="text-[10px] text-slate-500">{p.width}×{p.height} · {(p.size_bytes/1024).toFixed(0)} KB</div>
                    </div>
                    <span className={`text-[9.5px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded ${
                      p.verdict === "pass" ? "bg-emerald-200 text-emerald-800"
                        : p.verdict === "fail" ? "bg-red-200 text-red-800" : "bg-amber-200 text-amber-800"
                    }`}>{p.verdict}</span>
                  </div>
                  <ul className="text-[10.5px] space-y-0.5">
                    {(p.checks || []).map((c, j) => (
                      <li key={j} className="flex items-center justify-between">
                        <span className="text-slate-700">{c.name}</span>
                        <span className={`font-mono ${c.ok ? "text-emerald-700" : "text-red-700"}`}>{c.ok ? "✓" : "✗"} {String(c.value || "—").slice(0,40)}</span>
                      </li>
                    ))}
                  </ul>
                  {p.quality_flags?.length > 0 && (
                    <ul className="mt-2 text-[10px] text-slate-600 list-disc pl-3 space-y-0.5">
                      {p.quality_flags.map((f, j) => <li key={j}>{f}</li>)}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
          {photos.length === 0 && <div className="text-center text-sm text-slate-500 py-10">No photos match the current filter.</div>}
        </div>
      )}
      {lightbox && null}
    </div>
  );
}

